import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Plus, X, Trash2, Edit3, Star, FileText, Gift, Search, Film, Printer } from "lucide-react";
import { toast } from "sonner";
import { tierOf, CLUB_TIERS } from "@/lib/constants";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", birthday: "", preferred_language: "Deutsch", favorite_genres: [], favorite_movie_tmdb_id: null, favorite_movie_title: "", favorite_movie_backdrop_url: "", favorite_movie_poster_url: "", is_member: false, newsletter_subscribed: true, notes: "" };

export default function Guests() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/guests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/guests/${id}`); load(); };

  const birthdayRun = async () => {
    if (!window.confirm("Jetzt Geburtstagsgutscheine für alle Club-Mitglieder mit heutigem Geburtstag erstellen?")) return;
    try {
      const { data } = await api.post("/club/birthday-run-now");
      toast.success(`${data.count} Geburtstagsgutschein(e) ausgestellt`);
      load();
    } catch (e) { toast.error("Fehler beim Geburtstagslauf"); }
  };

  return (
    <div className="space-y-6" data-testid="guests-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">CRM</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Gäste & Mitglieder</h1>
        </div>
        <button data-testid="guest-add-btn" onClick={() => { setEdit({ ...EMPTY }); setOpen(true); }} className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82]">
          <Plus size={16} /> Neuer Gast
        </button>
      </div>
      <div className="flex justify-end">
        <button data-testid="birthday-run-btn" onClick={birthdayRun} className="flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded hover:bg-[#D4AF37] hover:text-black transitions-safe">
          <Gift size={14} /> Geburtstage heute prüfen
        </button>
      </div>
      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#0A0A0A] border-b border-[#1A1A1A] text-[#A8A398] text-xs uppercase tracking-widest">
            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">E-Mail</th><th className="text-left p-3">Telefon</th><th className="text-left p-3">Club</th><th className="text-left p-3">Mitglied</th><th className="text-left p-3">Newsletter</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((g) => {
              const t = tierOf(g.visits_count || 0);
              const nlOn = g.newsletter_subscribed !== false;
              const toggleNL = async () => {
                try { await api.patch(`/guests/${g.id}`, { ...g, newsletter_subscribed: !nlOn }); load(); }
                catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              };
              return (
              <tr key={g.id} className="border-b border-[#141414]">
                <td className="p-3">{g.first_name} {g.last_name}</td>
                <td className="p-3 text-[#A8A398]">{g.email || "—"}</td>
                <td className="p-3 text-[#A8A398]">{g.phone || "—"}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5" style={{ color: t.color, borderColor: t.color + "80" }}>{t.l} · {g.visits_count || 0} Besuche</span></td>
                <td className="p-3">{g.is_member ? <span className="text-[#D4AF37] inline-flex items-center gap-1"><Star size={12} /> {g.member_number}</span> : <span className="text-[#A8A398]">—</span>}</td>
                <td className="p-3">
                  <button data-testid={`guest-newsletter-${g.id}`} onClick={toggleNL} disabled={!g.email}
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border transitions-safe ${!g.email ? "opacity-30 cursor-not-allowed border-[#2A2A2A] text-[#575148]" : nlOn ? "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10" : "border-[#5B1319] text-[#EE9CA1] hover:bg-[#5B1319]/20"}`}
                    title={g.email ? (nlOn ? "Abbestellen" : "Abonnieren") : "Keine E-Mail hinterlegt"}>
                    {nlOn ? "An" : "Aus"}
                  </button>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {g.is_member && (
                    <>
                      <a href={`/club/${g.member_qr || g.member_number}`} target="_blank" rel="noreferrer"
                        data-testid={`guest-digital-${g.id}`}
                        className="text-xs px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37] rounded hover:bg-[#D4AF37] hover:text-black mr-2 inline-flex items-center gap-1"
                        title="Digitale Club-Karte">
                        📱 Digital
                      </a>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/guests/${g.id}/card/pdf`} target="_blank" rel="noreferrer"
                        onClick={(e) => { e.preventDefault(); openPdf(`/guests/${g.id}/card/pdf`); }}
                        data-testid={`guest-card-${g.id}`}
                        className="text-xs px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37] rounded hover:bg-[#D4AF37] hover:text-black mr-2 inline-flex items-center gap-1"
                        title="Club-Karte drucken">
                        <FileText size={12} /> PDF
                      </a>
                      <button onClick={async () => {
                          try { await api.post(`/guests/${g.id}/card/print-office`); toast.success("Karte an Bürodrucker gesendet"); }
                          catch (e) {
                            toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                            openPdf(`/guests/${g.id}/card/pdf`);
                          }
                        }}
                        data-testid={`guest-card-print-${g.id}`}
                        className="text-xs px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37] rounded hover:bg-[#D4AF37] hover:text-black mr-2 inline-flex items-center gap-1"
                        title="Mitgliederkarte am Bürodrucker drucken (öffnet PDF wenn Drucker offline)">
                        <Printer size={12} /> Drucken
                      </button>
                      {g.subscription?.status === "active" && (
                        <>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/guests/${g.id}/abo-card/pdf`} target="_blank" rel="noreferrer"
                            onClick={(e) => { e.preventDefault(); openPdf(`/guests/${g.id}/abo-card/pdf`); }}
                            data-testid={`guest-abo-card-${g.id}`}
                            className="text-xs px-2 py-1 border border-[#8FBC8F]/40 text-[#8FBC8F] rounded hover:bg-[#8FBC8F] hover:text-black mr-2 inline-flex items-center gap-1"
                            title="Abo-Karte als PDF">
                            <FileText size={12} /> Abo
                          </a>
                          <button onClick={async () => {
                              try { await api.post(`/guests/${g.id}/abo-card/print-office`); toast.success("Abo-Karte an Bürodrucker gesendet"); }
                              catch (e) {
                                toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                                openPdf(`/guests/${g.id}/abo-card/pdf`);
                              }
                            }}
                            data-testid={`guest-abo-card-print-${g.id}`}
                            className="text-xs px-2 py-1 border border-[#8FBC8F]/40 text-[#8FBC8F] rounded hover:bg-[#8FBC8F] hover:text-black mr-2 inline-flex items-center gap-1"
                            title="Abo-Karte am Bürodrucker drucken (öffnet PDF wenn Drucker offline)">
                            <Printer size={12} /> Abo drucken
                          </button>
                        </>
                      )}
                    </>
                  )}
                  <button onClick={() => { setEdit(g); setOpen(true); }} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] mr-2"><Edit3 size={12} /></button>
                  <button onClick={() => remove(g.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
                </td>
              </tr>
            );})}
            {!rows.length && <tr><td colSpan={7} className="p-8 text-center text-[#A8A398]">Keine Gäste</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
      {open && <GuestDialog initial={edit} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
function GuestDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.patch(`/guests/${form.id}`, form); else await api.post("/guests", form);
      toast.success("Gespeichert"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm p-4 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-3 sm:my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">Gast</div><button onClick={onClose}><X /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <F label="Vorname" v={form.first_name} on={upd("first_name")} />
          <F label="Nachname" v={form.last_name} on={upd("last_name")} />
          <F label="E-Mail" v={form.email} on={upd("email")} />
          <F label="Telefon" v={form.phone} on={upd("phone")} />
          <F label="Geburtstag" v={form.birthday} on={upd("birthday")} type="date" />
          <F label="Bevorzugte Sprache" v={form.preferred_language} on={upd("preferred_language")} />
          <div className="md:col-span-2">
            <F label="Lieblingsgenres (kommagetrennt)" v={(form.favorite_genres || []).join(", ")} on={(v) => upd("favorite_genres")(v.split(",").map(s => s.trim()).filter(Boolean))} />
          </div>
          <div className="md:col-span-2 border-t border-[#1A1A1A] pt-4">
            <FavoriteMoviePicker
              current={{
                title: form.favorite_movie_title,
                backdrop: form.favorite_movie_backdrop_url,
                poster: form.favorite_movie_poster_url,
                tmdb_id: form.favorite_movie_tmdb_id,
              }}
              onPick={(m) => {
                upd("favorite_movie_tmdb_id")(m?.id || null);
                upd("favorite_movie_title")(m?.title || "");
                upd("favorite_movie_backdrop_url")(m?.backdrop_url || "");
                upd("favorite_movie_poster_url")(m?.poster_url || "");
              }}
              onClear={() => {
                upd("favorite_movie_tmdb_id")(null);
                upd("favorite_movie_title")("");
                upd("favorite_movie_backdrop_url")("");
                upd("favorite_movie_poster_url")("");
              }}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Notizen</label>
            <textarea rows={2} value={form.notes || ""} onChange={(e) => upd("notes")(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input type="checkbox" checked={form.is_member} onChange={(e) => upd("is_member")(e.target.checked)} /> Mitgliedschaft aktiv
          </label>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input type="checkbox" data-testid="guest-newsletter-checkbox" checked={form.newsletter_subscribed !== false} onChange={(e) => upd("newsletter_subscribed")(e.target.checked)} /> Wochen-Newsletter abonniert
          </label>
        </div>
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#A8A398]">Abbrechen</button>
          <button onClick={save} disabled={busy} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82]">{busy ? "…" : "Speichern"}</button>
        </div>
      </div>
    </div>
  );
}
function F({ label, v, on, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
    </div>
  );
}

function FavoriteMoviePicker({ current, onPick, onClear }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/tmdb/search`, { params: { q } });
      setResults(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "TMDb-Suche fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#D4AF37] flex items-center gap-2">
        <Film size={12} /> Lieblingsfilm — erscheint als Blur-Backdrop auf der Club-Karte
      </label>

      {/* Aktuelle Auswahl */}
      {current?.title && (
        <div className="mt-2 flex items-center gap-3 border border-[#D4AF37]/40 bg-[#D4AF37]/5 rounded-lg p-3" data-testid="favorite-movie-selected">
          {current.poster && (
            <img src={current.poster} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-10 aspect-[2/3] object-cover rounded" />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm truncate">{current.title}</div>
            <div className="text-[9px] uppercase tracking-[0.28em] text-[#A8A398]">
              {current.backdrop ? "Backdrop verfügbar" : "Ohne Backdrop"}
            </div>
          </div>
          <button type="button" onClick={onClear} className="text-[10px] uppercase tracking-[0.28em] px-3 py-1.5 rounded-full border border-[#7A1921] text-[#EE9CA1] hover:bg-[#7A1921]/20">
            Entfernen
          </button>
        </div>
      )}

      {/* Suche */}
      <div className="mt-2 flex gap-2">
        <input
          data-testid="favorite-movie-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
          placeholder="TMDb-Suche · Filmtitel eingeben"
          className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]"
        />
        <button type="button" data-testid="favorite-movie-search-btn" onClick={search} disabled={busy || !q.trim()}
          className="px-4 py-2 rounded bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#EEDC82] disabled:opacity-50 flex items-center gap-2">
          <Search size={12} /> {busy ? "…" : "Suchen"}
        </button>
      </div>

      {/* Suchergebnisse */}
      {results.length > 0 && (
        <div className="mt-2 max-h-64 overflow-y-auto border border-[#1A1A1A] rounded-lg divide-y divide-[#141414]">
          {results.slice(0, 8).map((m) => (
            <button key={m.id} type="button" onClick={() => { onPick(m); setResults([]); setQ(""); }}
              data-testid={`favorite-movie-result-${m.id}`}
              className="w-full flex items-center gap-3 p-2.5 hover:bg-[#0F0F0F] transitions-safe text-left">
              {m.poster_url ? (
                <img src={m.poster_url} alt="" className="w-8 aspect-[2/3] object-cover rounded" />
              ) : (
                <div className="w-8 aspect-[2/3] bg-[#111] rounded flex items-center justify-center text-[#575148]"><Film size={12} /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{m.title || m.title_de || m.title_original}</div>
                <div className="text-[9px] uppercase tracking-[0.28em] text-[#575148]">
                  {m.release_date?.slice(0, 4) || m.year || ""}{m.backdrop_url ? " · Backdrop ✓" : ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
